// Week 7 — Realtime appstate listeners for owner + partner collaboration.
//
// Goal: when another tab/user edits an appstate slice (via saveSlice /
// saveMisc / saveOpts / saveAppState / saveAttachments), the currently
// visible tab should reflect that change within ~1s, without a loading
// spinner or full-page re-render flash.
//
// Approach:
//  1. Subscribe to each per-slice Firestore doc under
//     users/{effectiveUid}/data/appstate_* (plus attachments).
//  2. On every snapshot, skip the apply when:
//      (a) the snapshot is our own echo (see echoGuard.ts), OR
//      (b) the parsed slice is referentially/structurally identical
//          to what's already in the React Query cache.
//  3. Otherwise, `setQueryData` with an object-spread that REPLACES only
//     the changed slice — all other slices keep their identity, so
//     memoized components whose props didn't change don't re-render.
//
// What this does NOT do:
//  - CRDT or OT merging. Last-write-wins.
//  - Row-level preservation of uncontrolled inputs being edited. If
//    Partner A mid-types into an input and Owner B saves the same row
//    from another tab, Partner A's in-progress keystrokes can be
//    clobbered. Current mitigation: only the changed slice's array
//    replaces, so rows being typed into ELSEWHERE (other slices) are
//    unaffected. Future pass could add an editingRows set to diff-in
//    only non-focused rows; documented limitation for now.

import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { onSnapshot, type DocumentSnapshot } from 'firebase/firestore'
import { userDoc } from '../lib/firebase/firestore'
import { parseSlice } from '../lib/db/converters'
import { isSelfEcho } from '../lib/db/echoGuard'
import { qk } from './queryKeys'
import { useAuth } from './useAuth'
import type {
  ActivityLogEntry, Assignment, Attachment, Bid, CalendarEvent, ChangeOrder,
  CustomTemplate, Opts,
  PendingAssignExpense, Reminder, Sub, Vendor,
} from '../types/domain'
import type {
  AppState, AppstateMetaDoc, AppstateMiscDoc, AppstateSliceDoc, MiscPayload,
} from '../types/firestore'

// ── Helpers ────────────────────────────────────────────────────────────────

function parseMiscDoc(data: unknown): MiscPayload {
  if (!data || typeof (data as AppstateMiscDoc).json !== 'string') return {}
  try {
    const parsed = JSON.parse((data as AppstateMiscDoc).json as string)
    return parsed && typeof parsed === 'object' ? parsed as MiscPayload : {}
  } catch {
    return {}
  }
}

/** Shallow equality for arrays of records. Compares length first, then
 *  element-wise identity. Used only as a cheap pre-check before the
 *  JSON-string-equality fallback — most of the time arrays that LOOK the
 *  same come from different sources (new Firestore parse vs. loadAppState
 *  parse) so their elements won't be identity-equal even when content
 *  matches. That's fine; the string compare catches the deep-equal case. */
function arrayShallowEqual<T>(a: T[], b: T[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/** Deep-equality for two arrays via JSON.stringify. Used as the "content
 *  is identical, skip to avoid a re-render" check in snapshot handlers.
 *  Firestore slice docs ARE stringified already, so on the hot path we
 *  can compare incoming `data.json` to a JSON.stringify of the cached
 *  slice without re-parsing.
 *
 *  Cost: O(n) in the array length, once per snapshot per slice. A no-op
 *  echo of a typical "200-client" slice serializes to ~20–40 KB — a sub-
 *  millisecond call. Cheap vs. the cost of a React re-render tree. */
function jsonEqual<T>(cached: T[], incomingJson: string): boolean {
  // Normalize: empty cached arr vs incoming "[]" are equal.
  const lhs = JSON.stringify(cached ?? [])
  return lhs === incomingJson
}

/** Apply an incoming `appstate_<slice>` snapshot (the JSON-encoded array
 *  docs: clients, projects, estimates, expenses, payments, schedules) to
 *  the React Query cache, merging in-place without disturbing siblings. */
function applySliceSnap<K extends
  'clients' | 'projects' | 'estimates' | 'expenses' | 'payments' | 'schedules',
>(
  qc: QueryClient,
  uid: string,
  sliceKey: K,
  snap: DocumentSnapshot,
): void {
  if (!snap.exists()) return
  const data = snap.data() as AppstateSliceDoc | undefined
  if (!data) return

  if (isSelfEcho(sliceKey, data._updatedAtMs)) return

  const prev = qc.getQueryData<AppState>(qk.appstate(uid))
  if (!prev) return

  // Fast path: the snapshot's JSON string matches what we'd serialize
  // from the cached slice. Catches the initial-subscribe echo where
  // Firestore replays the doc we just loaded via loadAppState, as well as
  // duplicated snapshots from reconnects / strict-mode double-mount in
  // dev. Parse is skipped entirely in this path.
  if (jsonEqual(prev[sliceKey] as unknown[], data.json)) return

  type Row = AppState[K][number]
  const next = parseSlice<Row>(data.json) as AppState[K]
  // Belt-and-suspenders: even after a parse, if element identity matches
  // (e.g., cached array WAS the result of this parse already), skip.
  if (arrayShallowEqual(prev[sliceKey] as unknown[], next as unknown[])) return

  qc.setQueryData<AppState>(qk.appstate(uid), (curr) => {
    if (!curr) return curr
    return { ...curr, [sliceKey]: next }
  })
}

/** Apply incoming `appstate_misc` snapshot — merges EACH misc sub-array
 *  into the AppState cache. Misc contains: reminders, changeOrders, subs,
 *  vendors, customTemplates, assignments, pendingAssignExpenses,
 *  activityLog, bids, events. */
function applyMiscSnap(
  qc: QueryClient,
  uid: string,
  snap: DocumentSnapshot,
): void {
  if (!snap.exists()) return
  const data = snap.data() as AppstateMiscDoc | undefined
  if (!data) return

  if (isSelfEcho('misc', data._updatedAtMs)) return

  const prev = qc.getQueryData<AppState>(qk.appstate(uid))
  if (!prev) return

  // Fast path: compare the incoming misc JSON to a re-serialization of
  // our cached misc fields. Avoids the per-array arrayShallowEqual
  // cascade below on a no-op echo.
  if (typeof data.json === 'string') {
    const prevMiscPayload = {
      reminders:             prev.reminders,
      changeOrders:          prev.changeOrders,
      subs:                  prev.subs,
      vendors:               prev.vendors,
      customTemplates:       prev.customTemplates,
      assignments:           prev.assignments,
      pendingAssignExpenses: prev.pendingAssignExpenses,
      activityLog:           prev.activityLog,
      bids:                  prev.bids,
      events:                prev.events,
    }
    if (JSON.stringify(prevMiscPayload) === data.json) return
  }

  const parsed = parseMiscDoc(data)

  // Build replacement sub-arrays with empty-array defaults to match
  // what loadAppState does on cold read. The arr-shallow equality check
  // decides whether this snapshot actually changed anything.
  const nextMisc = {
    reminders:             (Array.isArray(parsed.reminders)             ? parsed.reminders             : []) as Reminder[],
    changeOrders:          (Array.isArray(parsed.changeOrders)          ? parsed.changeOrders          : []) as ChangeOrder[],
    subs:                  (Array.isArray(parsed.subs)                  ? parsed.subs                  : []) as Sub[],
    vendors:               (Array.isArray(parsed.vendors)               ? parsed.vendors               : []) as Vendor[],
    customTemplates:       (Array.isArray(parsed.customTemplates)       ? parsed.customTemplates       : []) as CustomTemplate[],
    assignments:           (Array.isArray(parsed.assignments)           ? parsed.assignments           : []) as Assignment[],
    pendingAssignExpenses: (Array.isArray(parsed.pendingAssignExpenses) ? parsed.pendingAssignExpenses : []) as PendingAssignExpense[],
    activityLog:           (Array.isArray(parsed.activityLog)           ? parsed.activityLog           : []) as ActivityLogEntry[],
    bids:                  (Array.isArray(parsed.bids)                  ? parsed.bids                  : []) as Bid[],
    events:                (Array.isArray(parsed.events)                ? parsed.events                : []) as CalendarEvent[],
  }

  const allEqual =
       arrayShallowEqual(prev.reminders,             nextMisc.reminders)
    && arrayShallowEqual(prev.changeOrders,          nextMisc.changeOrders)
    && arrayShallowEqual(prev.subs,                  nextMisc.subs)
    && arrayShallowEqual(prev.vendors,               nextMisc.vendors)
    && arrayShallowEqual(prev.customTemplates,       nextMisc.customTemplates)
    && arrayShallowEqual(prev.assignments,           nextMisc.assignments)
    && arrayShallowEqual(prev.pendingAssignExpenses, nextMisc.pendingAssignExpenses)
    && arrayShallowEqual(prev.activityLog,           nextMisc.activityLog)
    && arrayShallowEqual(prev.bids,                  nextMisc.bids)
    && arrayShallowEqual(prev.events,                nextMisc.events)
  if (allEqual) return

  qc.setQueryData<AppState>(qk.appstate(uid), (curr) => {
    if (!curr) return curr
    return { ...curr, ...nextMisc }
  })
}

/** Apply incoming `appstate_meta` snapshot (scalar counters + opts). */
function applyMetaSnap(
  qc: QueryClient,
  uid: string,
  snap: DocumentSnapshot,
): void {
  if (!snap.exists()) return
  const data = snap.data() as AppstateMetaDoc | undefined
  if (!data) return

  if (isSelfEcho('meta', data._updatedAtMs)) return

  const prev = qc.getQueryData<AppState>(qk.appstate(uid))
  if (!prev) return

  // Build a patch with only the fields we recognize — never dump raw doc
  // into the state or we'd leak firestore-internals. Keep existing values
  // when the doc omits a counter (matches loadAppState fallback logic).
  const nextOpts: Opts = { ...prev.opts, ...(data.opts ?? {}) }
  const patch: Partial<AppState> = {
    nextClientId:  data.nextClientId  ?? prev.nextClientId,
    nextProjId:    data.nextProjId    ?? prev.nextProjId,
    nextEstId:     data.nextEstId     ?? prev.nextEstId,
    nextEstItemId: data.nextEstItemId ?? prev.nextEstItemId,
    nextSubId:     data.nextSubId     ?? prev.nextSubId,
    nextVendorId:  data.nextVendorId  ?? prev.nextVendorId,
    nextCoId:      data.nextCoId      ?? prev.nextCoId,
    nextExpId:     data.nextExpId     ?? prev.nextExpId,
    nextBidId:     data.nextBidId     ?? prev.nextBidId,
    nextSchedId:   data.nextSchedId   ?? prev.nextSchedId,
    nextPayId:     data.nextPayId     ?? prev.nextPayId,
    nextRemId:     data.nextRemId     ?? prev.nextRemId,
    nextAttachId:  data.nextAttachId  ?? prev.nextAttachId,
    nextEventId:   data.nextEventId   ?? prev.nextEventId,
    opts:          nextOpts,
    version:       data.version ?? prev.version,
  }
  // Preserve exactOptionalPropertyTypes: only set activeOvProj when present.
  if (data.activeOvProj !== undefined) patch.activeOvProj = data.activeOvProj

  // Cheap dirty-check: nothing to write if every scalar and opts matches.
  // JSON.stringify on opts is fine — it's a small flat object.
  const sameCounters =
       patch.nextClientId  === prev.nextClientId
    && patch.nextProjId    === prev.nextProjId
    && patch.nextEstId     === prev.nextEstId
    && patch.nextEstItemId === prev.nextEstItemId
    && patch.nextSubId     === prev.nextSubId
    && patch.nextVendorId  === prev.nextVendorId
    && patch.nextCoId      === prev.nextCoId
    && patch.nextExpId     === prev.nextExpId
    && patch.nextBidId     === prev.nextBidId
    && patch.nextSchedId   === prev.nextSchedId
    && patch.nextPayId     === prev.nextPayId
    && patch.nextRemId     === prev.nextRemId
    && patch.nextAttachId  === prev.nextAttachId
    && patch.nextEventId   === prev.nextEventId
    && patch.version       === prev.version
    && patch.activeOvProj  === prev.activeOvProj
  const sameOpts = JSON.stringify(nextOpts) === JSON.stringify(prev.opts)
  if (sameCounters && sameOpts) return

  qc.setQueryData<AppState>(qk.appstate(uid), (curr) => {
    if (!curr) return curr
    return { ...curr, ...patch }
  })
}

/** Apply incoming `attachments` snapshot. Stored at users/{uid}/data/attachments
 *  (NOT appstate_attachments — see lib/db/appstate.ts saveAttachments). */
function applyAttachmentsSnap(
  qc: QueryClient,
  uid: string,
  snap: DocumentSnapshot,
): void {
  if (!snap.exists()) return
  const data = snap.data() as AppstateSliceDoc | undefined
  if (!data) return

  if (isSelfEcho('attachments', data._updatedAtMs)) return

  const prev = qc.getQueryData<AppState>(qk.appstate(uid))
  if (!prev) return
  if (jsonEqual(prev.attachments, data.json)) return
  const next = parseSlice<Attachment>(data.json)
  if (arrayShallowEqual(prev.attachments, next)) return

  qc.setQueryData<AppState>(qk.appstate(uid), (curr) => {
    if (!curr) return curr
    return { ...curr, attachments: next }
  })
}

// ── Hook ───────────────────────────────────────────────────────────────────

/**
 * Mounts per-slice Firestore snapshot listeners for the current effective
 * uid's appstate. No-op until both `effectiveUid` and `isReady` are set
 * (matches the load gate in useAppState). Re-subscribes when effectiveUid
 * changes (e.g., partner toggles access / signs out).
 */
export function useAppStateRealtime(): void {
  const { effectiveUid, isReady } = useAuth()
  const qc = useQueryClient()

  useEffect(() => {
    if (!effectiveUid || !isReady) return
    const uid = effectiveUid

    // Open one listener per slice doc. Each one only dispatches into the
    // single slice key it owns — no cross-slice contention.
    const unsubMeta = onSnapshot(
      userDoc(uid, 'appstate_meta'),
      (snap) => applyMetaSnap(qc, uid, snap),
      (err) => console.warn('[appstate-realtime] meta listener error', err),
    )
    const unsubClients = onSnapshot(
      userDoc(uid, 'appstate_clients'),
      (snap) => applySliceSnap(qc, uid, 'clients', snap),
      (err) => console.warn('[appstate-realtime] clients listener error', err),
    )
    const unsubProjects = onSnapshot(
      userDoc(uid, 'appstate_projects'),
      (snap) => applySliceSnap(qc, uid, 'projects', snap),
      (err) => console.warn('[appstate-realtime] projects listener error', err),
    )
    const unsubEstimates = onSnapshot(
      userDoc(uid, 'appstate_estimates'),
      (snap) => applySliceSnap(qc, uid, 'estimates', snap),
      (err) => console.warn('[appstate-realtime] estimates listener error', err),
    )
    const unsubExpenses = onSnapshot(
      userDoc(uid, 'appstate_expenses'),
      (snap) => applySliceSnap(qc, uid, 'expenses', snap),
      (err) => console.warn('[appstate-realtime] expenses listener error', err),
    )
    const unsubPayments = onSnapshot(
      userDoc(uid, 'appstate_payments'),
      (snap) => applySliceSnap(qc, uid, 'payments', snap),
      (err) => console.warn('[appstate-realtime] payments listener error', err),
    )
    const unsubSchedules = onSnapshot(
      userDoc(uid, 'appstate_schedules'),
      (snap) => applySliceSnap(qc, uid, 'schedules', snap),
      (err) => console.warn('[appstate-realtime] schedules listener error', err),
    )
    const unsubMisc = onSnapshot(
      userDoc(uid, 'appstate_misc'),
      (snap) => applyMiscSnap(qc, uid, snap),
      (err) => console.warn('[appstate-realtime] misc listener error', err),
    )
    const unsubAttachments = onSnapshot(
      userDoc(uid, 'attachments'),
      (snap) => applyAttachmentsSnap(qc, uid, snap),
      (err) => console.warn('[appstate-realtime] attachments listener error', err),
    )

    return () => {
      unsubMeta()
      unsubClients()
      unsubProjects()
      unsubEstimates()
      unsubExpenses()
      unsubPayments()
      unsubSchedules()
      unsubMisc()
      unsubAttachments()
    }
  }, [effectiveUid, isReady, qc])
}

// ── Internals exported for tests only ──────────────────────────────────────
export const __TEST_ONLY__ = {
  applySliceSnap,
  applyMiscSnap,
  applyMetaSnap,
  applyAttachmentsSnap,
  arrayShallowEqual,
  jsonEqual,
}
